import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ExternalLink, Search, AlertTriangle, Info, RefreshCw } from 'lucide-react';

/**
 * Marka dışı organik arama — hangi sorgu hangi sayfaya iniyor.
 *
 * Search Console sayfa+sorgu verisini marka / marka dışı ayırıp marka dışı
 * gösterimin nereye indiğini gösterir. Backend: GET /api/nonbrand-search.
 *
 * Sadece Europcar brand'inde görünür (App.js allowBrandIds + Sidebar
 * restrictedBrands).
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const nf = (n) => Number(n || 0).toLocaleString('tr-TR');
const pctOf = (a, b, d = 2) => (b > 0 ? `%${(a / b * 100).toFixed(d)}` : '—');
const dShort = (s) => (s ? new Date(s).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' }) : '—');

function Stat({ label, value, sub, tone }) {
  return (
    <div className="bg-white p-4">
      <div className={`text-[26px] font-bold leading-none tabular-nums ${tone === 'emerald' ? 'text-emerald-700' : 'text-slate-900'}`}>
        {value}
      </div>
      <div className="mt-1.5 text-[13px] text-slate-500">{label}</div>
      {sub ? <div className="mt-0.5 text-[12px] text-slate-400">{sub}</div> : null}
    </div>
  );
}

function SectionHead({ eyebrow, title, children }) {
  return (
    <div className="mb-4">
      <h2 className="text-[13px] font-bold uppercase tracking-wider text-emerald-700">{eyebrow}</h2>
      <p className="mt-1.5 text-[22px] font-bold leading-snug tracking-tight text-slate-900">{title}</p>
      {children ? <p className="mt-1.5 max-w-[74ch] text-[14px] leading-relaxed text-slate-500">{children}</p> : null}
    </div>
  );
}

export default function EuropcarNonBrandReport() {
  const brandId = typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null;
  const headers = useMemo(
    () => ({ 'Content-Type': 'application/json', ...(brandId ? { 'X-Brand-ID': brandId } : {}) }),
    [brandId]
  );

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/nonbrand-search?days=${days}&_t=${Date.now()}`, {
        credentials: 'include', headers
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'Veri yüklenemedi');
      setData(json);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [brandId, headers, days]);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};
  const totalImp = (s.nbImpressions || 0) + (s.brandImpressions || 0);
  const types = data?.types || [];
  const pages = data?.pages || [];
  const queries = data?.queries || [];
  const maxType = types[0]?.impressions || 0;
  // Çubuk ölçeği, en büyük sayfanın marka + marka dışı toplamına göre.
  const maxPage = pages.length ? pages[0].impressions + pages[0].brandImpressions : 0;
  const cov = data?.coverage;

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-[13px] font-bold uppercase tracking-wider text-emerald-700">Europcar · Organik Arama</h1>
          <p className="mt-1.5 text-[26px] font-bold leading-tight tracking-tight text-slate-900">
            Marka dışı aramalar hangi sayfalara iniyor
          </p>
          <p className="mt-1.5 max-w-[74ch] text-[14px] leading-relaxed text-slate-500">
            Search Console'un sayfa + sorgu verisi marka ve marka dışı olarak ayrılıyor. Sayfa raporu bir adresin ne
            kazandığını, sorgu raporu hangi kelimenin kazandırdığını söyler; "marka mı değil mi" sorusu ancak ikisinin
            kesiştiği bu tabloda cevaplanır.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex overflow-hidden rounded-lg border border-slate-200">
            {[30, 90].map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                className={`px-3 py-1.5 text-[13px] ${days === d ? 'bg-emerald-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
              >{d} gün</button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-[13px] font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Yenile
          </button>
        </div>
      </div>

      {data?.window?.start ? (
        <p className="mt-3 text-[12px] text-slate-400">
          {dShort(data.window.start)} – {dShort(data.window.end)} · Search Console'un son dolu günü {data.window.end}
        </p>
      ) : null}

      {/* Marka eşleşmesi — hangi kalıpla ayrıldığı görünür olmalı */}
      {data?.brandMatch ? (
        <div className="mt-4 flex flex-wrap items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
          <Info className="mt-0.5 h-4 w-4 flex-none text-slate-400" />
          <p className="text-[13px] leading-relaxed text-slate-600">
            {data.brandMatch.source === 'terms' ? (
              <>Marka sorguları, tanımlı <strong>{data.brandMatch.terms.length} marka terimiyle</strong> ayrıldı.</>
            ) : (
              <>
                Marka terimi listesi boş olduğu için kalıp marka adından türetildi:{' '}
                <code className="rounded bg-white px-1.5 py-0.5 text-[12px] text-slate-700">{data.brandMatch.pattern}</code>{' '}
                — sorgular harf dışı karakterlerden arındırılıp karşılaştırılıyor, böylece <em>eurocar</em>,{' '}
                <em>europ car</em>, <em>europecar</em> gibi yanlış yazımlar da marka sayılıyor.
              </>
            )}
          </p>
        </div>
      ) : null}

      <div className="mt-6 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 sm:grid-cols-3 lg:grid-cols-5">
        <Stat
          label="marka dışı payı"
          value={loading && !data ? '…' : pctOf(s.nbImpressions, totalImp, 1)}
          sub={`marka: ${pctOf(s.brandImpressions, totalImp, 1)}`}
          tone="emerald"
        />
        <Stat label="marka dışı gösterim" value={nf(s.nbImpressions)} sub={`${nf(s.nbQueries)} ayrı sorgu`} />
        <Stat label="marka dışı tık" value={nf(s.nbClicks)} sub={`CTR ${pctOf(s.nbClicks, s.nbImpressions)}`} />
        <Stat label="marka CTR'ı" value={pctOf(s.brandClicks, s.brandImpressions)} sub={`${nf(s.brandClicks)} tık`} />
        <Stat
          label="en yoğun bölüm"
          value={types[0] ? types[0].label : '—'}
          sub={types[0] ? `${pctOf(types[0].impressions, s.nbImpressions, 1)} · ${nf(types[0].pages)} sayfa` : null}
        />
      </div>

      {/* Sayfa tipleri */}
      {types.length > 0 && (
        <section className="mt-10">
          <SectionHead eyebrow="Bölümler" title="Marka dışı gösterim nereye iniyor">
            Adresin ilk bölümüne göre gruplandı. Sağdaki CTR aynı bölümün marka dışı tıklama oranı — hacim ile verim
            aynı yerde durmuyor.
          </SectionHead>
          <div className="overflow-hidden rounded-xl border border-slate-200">
            {types.map(t => (
              <div key={t.label} className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-100 bg-white p-3 last:border-0">
                <div className="min-w-[190px] flex-1">
                  <div className="text-[14px] font-semibold text-slate-900">{t.label}</div>
                  <div className="text-[11.5px] tabular-nums text-slate-400">
                    {nf(t.pages)} sayfa · marka gösterimi {nf(t.brandImpressions)}
                  </div>
                </div>
                <div className="hidden h-2.5 w-[260px] flex-none overflow-hidden rounded bg-slate-100 sm:block">
                  <div className="h-full rounded-r bg-emerald-600" style={{ width: `${maxType ? t.impressions / maxType * 100 : 0}%` }} />
                </div>
                <div className="w-[150px] flex-none text-right">
                  <div className="text-[14.5px] font-semibold tabular-nums text-slate-900">{nf(t.impressions)}</div>
                  <div className="text-[11.5px] tabular-nums text-slate-400">
                    {pctOf(t.impressions, s.nbImpressions, 1)} · CTR {pctOf(t.clicks, t.impressions)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Sayfalar */}
      {pages.length > 0 && (
        <section className="mt-10">
          <SectionHead eyebrow="Sayfalar" title="En çok marka dışı gösterim alan 25 sayfa">
            Her sayfanın marka dışı (koyu) ve marka (açık) gösterimi yan yana. Marka payı yüksek olan sayfa, kendi
            başına arama getirmiyor demektir.
          </SectionHead>
          <div className="mb-2 flex gap-4 text-[11.5px] uppercase tracking-wider text-slate-500">
            <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 bg-emerald-600" /> Marka dışı</span>
            <span className="inline-flex items-center gap-1.5"><i className="h-2.5 w-2.5 bg-sky-400" /> Marka</span>
          </div>
          <div className="overflow-hidden rounded-xl border border-slate-200">
            {pages.map(p => (
              <div key={p.path} className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-slate-100 bg-white p-3 last:border-0">
                <div className="min-w-[220px] flex-1">
                  <a
                    href={`https://www.europcar.com.tr${p.path}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-start gap-1 break-all text-[13.5px] font-medium text-slate-800 hover:text-emerald-700"
                  >
                    {p.path}
                    <ExternalLink className="mt-0.5 h-3 w-3 flex-none opacity-30" />
                  </a>
                  <div className="text-[11.5px] tabular-nums text-slate-400">
                    {nf(p.queries)} marka dışı sorgu · {nf(p.clicks)} tık · CTR {pctOf(p.clicks, p.impressions)}
                  </div>
                </div>
                <div className="hidden w-[260px] flex-none items-center gap-0.5 sm:flex">
                  <div className="h-2.5 rounded-r bg-emerald-600" style={{ width: `${maxPage ? p.impressions / maxPage * 100 : 0}%` }} />
                  <div className="h-2.5 rounded-r bg-sky-400" style={{ width: `${maxPage ? p.brandImpressions / maxPage * 100 : 0}%` }} />
                </div>
                <div className="w-[150px] flex-none text-right">
                  <div className="text-[14.5px] font-semibold tabular-nums text-slate-900">{nf(p.impressions)}</div>
                  <div className="text-[11.5px] tabular-nums text-slate-400">
                    marka {nf(p.brandImpressions)} · {pctOf(p.brandImpressions, p.impressions + p.brandImpressions, 0)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Sorgular */}
      <section className="mt-10">
        <SectionHead eyebrow="Sorgular" title="En çok gösterim alan marka dışı sorgular ve indikleri sayfa">
          "İndiği sayfa", o sorgunun gösteriminin en çoğunu alan adres; yanındaki oran o sayfanın payı. "Dağılım",
          sorgunun kaç ayrı sayfa arasında bölündüğü — yüksek sayı, Google'ın hangi sayfayı göstereceğine karar
          veremediği anlamına gelir.
        </SectionHead>
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full min-w-[900px] border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-800 bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-2.5 text-left">Sorgu</th>
                <th className="p-2.5 text-right">Gösterim</th>
                <th className="p-2.5 text-right">Tık</th>
                <th className="p-2.5 text-right">CTR</th>
                <th className="p-2.5 text-right">Poz.</th>
                <th className="p-2.5 text-left">İndiği sayfa</th>
                <th className="p-2.5 text-right">Pay</th>
                <th className="p-2.5 text-right">Dağılım</th>
              </tr>
            </thead>
            <tbody>
              {loading && !queries.length ? (
                <tr><td colSpan={8} className="p-8 text-center text-slate-400">Yükleniyor…</td></tr>
              ) : !queries.length ? (
                <tr>
                  <td colSpan={8} className="p-8 text-center text-slate-500">
                    <Search className="mx-auto mb-2 h-6 w-6 text-slate-300" />
                    Bu pencerede marka dışı sorgu verisi yok.
                  </td>
                </tr>
              ) : queries.map(q => (
                <tr key={q.query} className="border-b border-slate-100 last:border-0 hover:bg-emerald-50/40">
                  <td className="max-w-[280px] p-2.5 text-[13.5px] font-medium text-slate-800">
                    {q.query}
                    {q.clicks === 0 && (
                      <span className="ml-2 whitespace-nowrap rounded border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[10.5px] font-semibold uppercase text-rose-600">
                        0 tık
                      </span>
                    )}
                  </td>
                  <td className="p-2.5 text-right text-[13px] font-semibold tabular-nums text-slate-900">{nf(q.impressions)}</td>
                  <td className={`p-2.5 text-right text-[13px] tabular-nums ${q.clicks === 0 ? 'font-semibold text-rose-500' : 'text-slate-700'}`}>{nf(q.clicks)}</td>
                  <td className="p-2.5 text-right text-[12.5px] tabular-nums text-slate-500">{pctOf(q.clicks, q.impressions)}</td>
                  <td className="p-2.5 text-right text-[12.5px] tabular-nums text-slate-500">{q.position == null ? '—' : q.position.toFixed(1)}</td>
                  <td className="max-w-[300px] p-2.5">
                    <a
                      href={`https://www.europcar.com.tr${q.topPath}`}
                      target="_blank" rel="noopener noreferrer"
                      className="break-all text-[12px] text-slate-600 hover:text-emerald-700"
                    >{q.topPath}</a>
                  </td>
                  <td className="p-2.5 text-right text-[12.5px] tabular-nums text-slate-500">{pctOf(q.topImpressions, q.impressions, 0)}</td>
                  <td className="whitespace-nowrap p-2.5 text-right text-[12.5px] tabular-nums">
                    <span className={q.spread >= 25 ? 'font-semibold text-amber-600' : 'text-slate-500'}>{q.spread} sayfa</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Kapsam uyarısı */}
      {cov?.pageImpressions ? (
        <div className="mt-8 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-amber-500" />
          <p className="max-w-[80ch] text-[13px] leading-relaxed text-amber-900">
            <strong>Kapsam:</strong> Sayfa + sorgu raporu Search Console'da anonimleştirme nedeniyle eksiktir. Bu
            pencerede sayfa raporunun <strong>{pctOf(cov.pageQueryImpressions, cov.pageImpressions, 1)}</strong>'ini
            görüyor ({nf(cov.pageQueryImpressions)} / {nf(cov.pageImpressions)} gösterim). Uzun kuyruklu sayfalarda oran
            daha da düşer — bu yüzden buradaki blog rakamları Blog Envanteri sayfasındakinden düşüktür. İkisi farklı
            rapor, biri diğerinin hatası değil.
          </p>
        </div>
      ) : null}
    </div>
  );
}
